import flatten from 'lodash/flatten';
import groupBy from 'lodash/groupBy';
import uniq from 'lodash/uniq';
import chunk from 'lodash/chunk';
import uuid from 'uuid';
// import { executeMutation } from '@lyearn-team/content-processing-library';
import executeMutationWrapper from 'helper/executeMutationWrapper';

import { user } from '../../currentUser';
import { getUpdatedItemIdsByIndex, getUpdatedItemsByIndex } from '../../getUpdatedUniqueItemIds';
import * as TinyPubSub from '../../TinyPubSub';
import asyncForEach from '../../asyncForEach';
import { blockUserInteraction } from '../../blockUserInteractionHelper';

import { fetchStatesRemotelyFromUserIdContentIdObject } from '../DataService';

import {
  DataSyncStatus,
  getMultipleStatesById,
  saveStates,
  saveAllEnrolments,
  saveStateInteractions,
  saveAllDataSyncObjects,
  getDataSyncObjectByMultipleIds,
  removeMultipleDataSyncObjectsWithId
} from '../LocalStore';

import { SYNC_OBJECT_COMMANDS_LIMIT } from '../../Constants';
import { fetchLearnerOfferLocally, fetchLearnerOfferRemotely } from '../fetchOffer';

function getAllDataSyncIds(messages) {
  return messages.map(m => m.dataSyncId || '').filter(m => !!m);
}

function mergeAllStaleStateInfo(messages) {
  return flatten(
    messages
      .map(m => {
        try {
          return JSON.parse(m.message || '{}').staleStatesInfo || [];
        } catch (e) {
          console.log('invalid message in response of Failed subscription', e, m);
          return null;
        }
      })
      .filter(m => !!m)
  );
}

/* const refetchAllStatesFromDSOs = async (dsos) => {
  const allStateIds = uniq(flatten(dsos.map(dso => dso.stateIds)));
  const allStates = await getMultipleStatesById(allStateIds);

  const allStateIdsObject = allStates.map(state => ({
    userId: state.userId,
    rootContentId_contentId: state.rootContentId_contentId
  }));
  return fetchStatesRemotelyFromUserIdContentIdObject(allStateIdsObject);
}; */

function getAllCommands(DSOs) {
  return flatten(DSOs.map(d => d.commands || []));
}

const fetchAllOfferAndRewriteStates = async dataSyncObjects => {
  if (dataSyncObjects.length < 1) {
    return Promise.resolve(null);
  }

  const allCommands = getAllCommands(dataSyncObjects);
  const allOfferIds = uniq(allCommands.map(command => command.rootContentId));
  if (allOfferIds.length < 1) {
    return Promise.resolve(null);
  }
  const userId = dataSyncObjects[0].userId;
  return Promise.all(allOfferIds.map(offerId => fetchLearnerOfferRemotely(offerId, userId, true)));
};

function sortByKinesisWriteTS(obj1, obj2) {
  return obj1.kinesisWriteTS - obj2.kinesisWriteTS;
}

async function refetchAndSaveStates(staleStateInfos) {
  const stateIds = uniq(staleStateInfos.map(state => `${state.userId}_${state.contentId}`));
  const failedStates = await getMultipleStatesById(stateIds);
  const failedStateIdObject = failedStates.map(state => ({
    userId: state.userId,
    rootContentId_contentId: state.rootContentId_contentId
  }));
  return fetchStatesRemotelyFromUserIdContentIdObject(failedStateIdObject);
}

export default async function handleErrorDSOs(
  errorDSOMessages,
  { doNotReportonANalytics /* , refetchAllStates */ } = {}
) {
  // Implement error DSOs
  // - messages
  //   - merge all stateStateIds
  //     - staleStates -> refetch and save
  //   - get all DSO from DSO-Ids
  //     - get all commands from DSOs
  //       - group commands by offerId
  //         - Replay commands per offerId
  //         - Save new DSO

  blockUserInteraction(true);
  const errorDSOMessagesF = errorDSOMessages.filter(m => !!m);
  const allStaleStateInfos = mergeAllStaleStateInfo(errorDSOMessagesF);
  await refetchAndSaveStates(allStaleStateInfos);

  const allDataSyncIds = getAllDataSyncIds(errorDSOMessagesF);
  const allDataSyncObjects = (await getDataSyncObjectByMultipleIds(allDataSyncIds)).filter(
    d => !!d
  );

  if (!allDataSyncObjects || allDataSyncObjects.length < 1) {
    blockUserInteraction(false);
    return false;
  }

  /*  if (refetchAllStates) {
    await refetchAllStatesFromDSOs(allDataSyncObjects);
  } */

  await fetchAllOfferAndRewriteStates(allDataSyncObjects);

  allDataSyncObjects.sort(sortByKinesisWriteTS);

  !doNotReportonANalytics &&
    allDataSyncIds.length &&
    window.analytics &&
    window.analytics.track('DATA_SYNC_ERROR', {
      dataSyncId: allDataSyncIds,
      statesLength: allStaleStateInfos.length,
      failedStates: allStaleStateInfos
    });

  const currentUserId = user.id();
  const currentCompanyId = user.companyId();

  const dataSyncObjectsGroupedByUserIdCompanyId = groupBy(allDataSyncObjects, d => {
    if (d.userId && d.companyId) return `${d.userId}_${d.companyId}`;
    return `${currentUserId}_${currentCompanyId}`;
  });

  await Promise.all(
    Object.keys(dataSyncObjectsGroupedByUserIdCompanyId).map(async userIdCompanyId => {
      const dataSyncObjects = dataSyncObjectsGroupedByUserIdCompanyId[userIdCompanyId];
      const allCommands = getAllCommands(dataSyncObjects);
      const commandsGroupedByOfferId = groupBy(allCommands, 'rootContentId');

      const allOfferIds = Object.keys(commandsGroupedByOfferId);

      const [dataSyncUserId, dataSyncCompanyId] = userIdCompanyId.split('_');
      const userObject = { userId: dataSyncUserId, companyId: dataSyncCompanyId };

      await Promise.all(
        allOfferIds.map(async offerId => {
          if (!offerId) {
            // There are commands without rootContentId
            return;
          }

          const commandsForOffer = commandsGroupedByOfferId[offerId];

          const {
            offer,
            enrolments: offerEnrolments,
            states: offerStates,
            stateInteractions: offerStateInteractions,
            curriculumOffers
          } = await fetchLearnerOfferLocally(offerId, dataSyncUserId);

          let allEvents = [];
          let allStates = offerStates;
          let allStateInteractions = offerStateInteractions;
          let allEnrollments = offerEnrolments;

          const newDataSyncObjects = [];

          const chunkedCommands = chunk(commandsForOffer, SYNC_OBJECT_COMMANDS_LIMIT);

          await asyncForEach(chunkedCommands, async commandChunk => {
            const initialStates = allStates;
            const initialEnrolments = allEnrollments;
            const initalStateInteractions = allStateInteractions;
            await asyncForEach(commandChunk, async command => {
              const {
                events,
                states: updatedStates,
                stateInteractions,
                enrolments
              } = executeMutationWrapper(
                command,
                userObject,
                offer,
                curriculumOffers,
                [],
                allStates,
                allEnrollments,
                allStateInteractions
              );

              await saveStates(getUpdatedItemsByIndex(allStates, updatedStates));
              await saveAllEnrolments(getUpdatedItemsByIndex(allEnrollments, enrolments));
              await saveStateInteractions(
                getUpdatedItemsByIndex(allStateInteractions, stateInteractions)
              );

              allEvents = [...events];
              allStates = updatedStates;
              allStateInteractions = stateInteractions;
              allEnrollments = enrolments;
            });

            const dataSyncObject = {
              id: uuid(),
              ...userObject,
              commands: commandChunk,
              events: allEvents,
              stateIds: getUpdatedItemIdsByIndex(initialStates, allStates, 'userId_contentId'),
              enrollmentIds: getUpdatedItemIdsByIndex(
                initialEnrolments,
                allEnrollments,
                'userId_contentId'
              ),
              stateInteractionIds: getUpdatedItemIdsByIndex(
                initalStateInteractions,
                allStateInteractions,
                'contentId_interactionType_interactingOnId_actorId'
              ),
              status: DataSyncStatus.QUEUED,
              timeStamp: new Date().toISOString(),
              createdFromSessionToken: window.userSessionId
            };

            newDataSyncObjects.push(dataSyncObject);
          });

          await saveAllDataSyncObjects(newDataSyncObjects);
          TinyPubSub.publish('oneDataSyncObjectsUpdated'); // eslint-disable-line new-cap
        })
      );
    })
  );

  await removeMultipleDataSyncObjectsWithId(allDataSyncIds);
  blockUserInteraction(false);
}
